<template>
  <div
    ref="dropdownRef"
    class="relative w-full h-full"
  >
    <div
      class="w-full h-full flex items-center px-[16px] cursor-pointer"
      :class="{ 'opacity-50 cursor-not-allowed': disabled }"
      @click="toggleDropdown"
    >
      <span
        class="text-[13px] font-raleway truncate select-none"
        :class="modelValue ? 'text-black' : 'text-[#5b5c60]'"
        :title="rawSelectedLabel"
      >
        {{ selectedLabel }}
      </span>
      <div
        v-if="!modelValue"
        class="absolute inset-y-0 right-4 flex items-center text-black pointer-events-none"
      >
        <svg
          class="h-4 w-4 transition-transform duration-200"
          :class="{ 'rotate-180': isOpen }"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        ><path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 9l-7 7-7-7"
        /></svg>
      </div>
    </div>

    <!-- Dropdown List -->
    <transition name="fade">
      <div
        v-if="isOpen"
        class="absolute left-0 right-0 top-full mt-2 bg-white border border-black rounded-[21px] z-[100] p-2 shadow-lg max-h-[220px] overflow-y-auto doccu-scrollbar"
      >
        <ul class="flex flex-col">
          <li
            v-if="!options || options.length === 0"
            class="px-4 py-2 text-[13px] font-raleway text-[#5b5c60] text-center"
          >
            No options available
          </li>
          <li
            v-for="option in options"
            :key="option.id"
            class="px-4 py-2 text-[13px] font-raleway text-black hover:bg-[#eef1f0] rounded-[14px] cursor-pointer transition-colors"
            :class="{ 'bg-[#eef1f0] font-bold': option.id === modelValue }"
            :title="option.fullName || option.name"
            @click="selectOption(option)"
          >
            {{ option.name.length > 60 ? option.name.substring(0, 60) + '...' : option.name }}
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  options: {
    type: Array,
    default: () => []
  },
  placeholder: {
    type: String,
    default: 'Select'
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const isOpen = ref(false)
const dropdownRef = ref(null)

const rawSelectedLabel = computed(() => {
  if (!props.modelValue) return props.placeholder
  const selected = props.options.find(opt => opt.id === props.modelValue)
  return selected ? selected.name : props.placeholder
})

const selectedLabel = computed(() => {
  const lbl = rawSelectedLabel.value
  return lbl.length > 60 ? lbl.substring(0, 60) + '...' : lbl
})

const toggleDropdown = () => {
  if (props.disabled) return
  isOpen.value = !isOpen.value
}

const selectOption = (option) => {
  emit('update:modelValue', option.id)
  isOpen.value = false
}

const handleClickOutside = (event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    isOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

.doccu-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.doccu-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.doccu-scrollbar::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 10px;
}
</style>
